import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MessageService } from 'primeng/api';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { CustomerService } from '../../customer/customer.service';
import { Role } from '../../customer/customer-role.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { TenantScopeService } from '../tenant-scope.service';
import {
    SisterCompanyAccessContext,
    SisterCompanyAccessRequest,
    SisterCompanyAccessRequestableCustomer
} from '../tenant-access.model';
import { SisterCompanyAccessService } from '../sister-company-access.service';

@Component({
    selector: 'app-sister-company-access',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        MultiSelectModule,
        SelectModule,
        TableModule,
        TagModule,
        ToastModule,
        TooltipModule
    ],
    providers: [MessageService],
    template: `
        <p-toast></p-toast>

        <div class="sister-access-shell">
            <div class="sister-access-note">
                <i class="pi pi-shield"></i>
                <span>
                    Requesting access never shares users or data between tenants. The target tenant’s administrator
                    selects the final ERP roles and can later change or revoke them.
                </span>
            </div>

            <section class="sister-section">
                <div class="section-heading">
                    <div>
                        <h4>Request sister-company access</h4>
                        <p>Request access for yourself, or for a user in <strong>{{ context?.homeCompanyName || 'your home tenant' }}</strong>.</p>
                    </div>
                    <p-button type="button" icon="pi pi-refresh" label="Refresh tenant access" severity="secondary"
                        [outlined]="true" (click)="refreshTenantAccess()"></p-button>
                </div>

                <div class="request-grid">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-select id="sisterAccessSubject" [options]="subjectOptions" [(ngModel)]="subjectCustomerId"
                                optionLabel="label" optionValue="value" [filter]="true" [showClear]="false"
                                appendTo="body" styleClass="w-full"></p-select>
                            <label for="sisterAccessSubject">Employee</label>
                        </p-iftalabel>
                    </p-inputGroup>

                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-select id="sisterAccessTarget" [options]="sisterCompanyOptions" [(ngModel)]="targetCompanyId"
                                optionLabel="label" optionValue="value" [filter]="true" [showClear]="true"
                                appendTo="body" styleClass="w-full"></p-select>
                            <label for="sisterAccessTarget">Sister tenant</label>
                        </p-iftalabel>
                    </p-inputGroup>

                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-multiSelect inputId="sisterAccessRoles" [options]="companyRoleOptions" [(ngModel)]="requestedRoleIds"
                                optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-multiSelect>
                            <label for="sisterAccessRoles">Requested ERP roles</label>
                        </p-iftalabel>
                    </p-inputGroup>

                    <p-iftalabel>
                        <textarea id="sisterAccessReason" class="w-full reason-input" rows="2" [(ngModel)]="requestReason"></textarea>
                        <label for="sisterAccessReason">Reason (optional)</label>
                    </p-iftalabel>
                </div>
                <div class="request-actions">
                    <p-button type="button" icon="pi pi-send" label="Submit request" [disabled]="!canSubmitRequest()"
                        (click)="submitRequest()"></p-button>
                </div>
            </section>

            <section class="sister-section">
                <div class="section-heading">
                    <div><h4>Outgoing requests</h4><p>Requests submitted by you, or by your home-tenant administrators.</p></div>
                    <p-button type="button" icon="pi pi-refresh" severity="secondary" [text]="true" label="Refresh"
                        (click)="loadRequests()"></p-button>
                </div>
                <p-table [value]="outbox" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '70rem' }">
                    <ng-template pTemplate="header">
                        <tr><th>Employee</th><th>Target tenant</th><th>Requested roles</th><th>Status</th><th>Submitted</th><th>Decision</th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td><div class="person-cell"><strong>{{ row.subjectCustomerName || ('Customer ' + row.subjectCustomerId) }}</strong><span>{{ row.subjectCustomerEmail }}</span></div></td>
                            <td>{{ row.targetCompanyName || ('Tenant ' + row.targetCompanyId) }}</td>
                            <td><p-tag *ngFor="let role of row.requestedRoles" [value]="role.roleName" severity="info"></p-tag></td>
                            <td><p-tag [value]="row.status" [severity]="statusSeverity(row.status)"></p-tag></td>
                            <td>{{ row.requestedAt | date:'medium' }}</td>
                            <td>
                                <span *ngIf="row.approvedRoles?.length"><p-tag *ngFor="let role of row.approvedRoles" [value]="role.roleName" severity="success"></p-tag></span>
                                <span *ngIf="row.decisionReason" class="decision-text">{{ row.decisionReason }}</span>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage"><tr><td colspan="6" class="text-center p-4">No sister-company access requests.</td></tr></ng-template>
                </p-table>
            </section>

            <section *ngIf="context?.canManageIncoming" class="sister-section">
                <div class="section-heading">
                    <div><h4>Incoming requests</h4><p>Approve only the roles required in your home tenant.</p></div>
                    <p-button type="button" icon="pi pi-refresh" severity="secondary" [text]="true" label="Refresh"
                        (click)="loadRequests()"></p-button>
                </div>
                <p-table [value]="inbox" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '78rem' }">
                    <ng-template pTemplate="header">
                        <tr><th>Employee & source</th><th>Requested roles</th><th>Reason</th><th>Status</th><th>Target-tenant roles</th><th class="text-right">Actions</th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td><div class="person-cell"><strong>{{ row.subjectCustomerName || ('Customer ' + row.subjectCustomerId) }}</strong><span>{{ row.sourceCompanyName }} · {{ row.subjectCustomerEmail }}</span></div></td>
                            <td><p-tag *ngFor="let role of row.requestedRoles" [value]="role.roleName" severity="info"></p-tag></td>
                            <td>{{ row.requestReason || '—' }}</td>
                            <td><p-tag [value]="row.status" [severity]="statusSeverity(row.status)"></p-tag></td>
                            <td>
                                <ng-container *ngIf="editingRequest?.id === row.id; else approvedRoles">
                                    <p-multiSelect [options]="companyRoleOptions" [(ngModel)]="finalRoleIds" optionLabel="label" optionValue="value"
                                        appendTo="body" styleClass="w-full"></p-multiSelect>
                                    <textarea class="w-full reason-input compact" rows="2" [(ngModel)]="decisionReason" placeholder="Decision note (optional)"></textarea>
                                </ng-container>
                                <ng-template #approvedRoles><p-tag *ngFor="let role of row.approvedRoles" [value]="role.roleName" severity="success"></p-tag></ng-template>
                            </td>
                            <td class="text-right action-cell">
                                <ng-container *ngIf="row.status === 'PENDING'">
                                    <p-button *ngIf="editingRequest?.id !== row.id" type="button" icon="pi pi-check" label="Review" [text]="true" (click)="beginReview(row)"></p-button>
                                    <p-button *ngIf="editingRequest?.id === row.id" type="button" icon="pi pi-check" label="Approve" [disabled]="finalRoleIds.length === 0" (click)="approve(row)"></p-button>
                                    <p-button *ngIf="editingRequest?.id === row.id" type="button" icon="pi pi-times" label="Cancel" severity="secondary" [text]="true" (click)="cancelReview()"></p-button>
                                    <p-button type="button" icon="pi pi-ban" label="Reject" severity="danger" [text]="true" (click)="reject(row)"></p-button>
                                </ng-container>
                                <ng-container *ngIf="row.status === 'APPROVED'">
                                    <p-button *ngIf="editingRequest?.id !== row.id" type="button" icon="pi pi-pencil" label="Roles" [text]="true" (click)="beginReview(row)"></p-button>
                                    <p-button *ngIf="editingRequest?.id === row.id" type="button" icon="pi pi-save" label="Save" [disabled]="finalRoleIds.length === 0" (click)="amendRoles(row)"></p-button>
                                    <p-button *ngIf="editingRequest?.id === row.id" type="button" icon="pi pi-times" label="Cancel" severity="secondary" [text]="true" (click)="cancelReview()"></p-button>
                                    <p-button type="button" icon="pi pi-trash" label="Revoke" severity="danger" [text]="true" (click)="revoke(row)"></p-button>
                                </ng-container>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage"><tr><td colspan="6" class="text-center p-4">No incoming requests for your home tenant.</td></tr></ng-template>
                </p-table>
            </section>
        </div>
    `,
    styles: [`
        .sister-access-shell { display: grid; gap: 1.25rem; }
        .sister-access-note { display: flex; gap: .55rem; padding: .85rem; border-radius: .5rem; background: color-mix(in srgb, var(--primary-color) 8%, transparent); color: var(--text-color-secondary); }
        .sister-section { display: grid; gap: .85rem; }
        .section-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; }
        .section-heading h4 { margin: 0 0 .2rem; }
        .section-heading p { margin: 0; color: var(--text-color-secondary); }
        .request-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .8rem; }
        .request-grid > :nth-child(3), .request-grid > :nth-child(4) { grid-column: span 2; }
        .request-actions { display: flex; justify-content: flex-end; }
        .reason-input { resize: vertical; border: 1px solid var(--surface-border); border-radius: var(--border-radius); padding: .7rem; background: var(--surface-ground); color: var(--text-color); }
        .reason-input.compact { margin-top: .45rem; min-width: 14rem; }
        .person-cell { display: grid; gap: .15rem; }
        .person-cell span, .decision-text { color: var(--text-color-secondary); font-size: .85rem; }
        :host ::ng-deep .p-tag { margin: .12rem .2rem .12rem 0; }
        .action-cell { white-space: nowrap; }
        @media (max-width: 760px) {
            .section-heading { flex-direction: column; }
            .request-grid { grid-template-columns: 1fr; }
            .request-grid > :nth-child(3), .request-grid > :nth-child(4) { grid-column: auto; }
        }
    `]
})
export class SisterCompanyAccessComponent implements OnInit {
    context: SisterCompanyAccessContext | null = null;
    roles: Role[] = [];
    outbox: SisterCompanyAccessRequest[] = [];
    inbox: SisterCompanyAccessRequest[] = [];
    subjectCustomerId?: number;
    targetCompanyId?: number;
    requestedRoleIds: number[] = [];
    requestReason = '';
    editingRequest: SisterCompanyAccessRequest | null = null;
    finalRoleIds: number[] = [];
    decisionReason = '';

    constructor(
        private readonly sisterCompanyAccessService: SisterCompanyAccessService,
        private readonly customerService: CustomerService,
        private readonly sessionStorageService: SessionStorageService,
        private readonly tenantScopeService: TenantScopeService,
        private readonly messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.loadContext();
        this.customerService.getAllRoles('ANY').subscribe({
            next: roles => this.roles = (roles ?? []).filter(role => !!role.roleId),
            error: () => this.roles = []
        });
    }

    get subjectOptions() {
        const current = this.sessionStorageService.getCustomer();
        const subjects = new Map<number, SisterCompanyAccessRequestableCustomer>();
        if (current?.id) {
            subjects.set(current.id, {
                customerId: current.id,
                customerName: current.name,
                customerEmail: current.emailAddress
            });
        }
        if (this.context?.canRequestForOthers) {
            (this.context.requestableCustomers ?? []).forEach(customer => {
                if (customer?.customerId) {
                    subjects.set(customer.customerId, customer);
                }
            });
        }
        return [...subjects.values()].map(customer => ({ label: this.customerLabel(customer), value: customer.customerId }));
    }

    get sisterCompanyOptions() {
        return (this.context?.sisterCompanies ?? []).map(company => ({ label: company.name || `Tenant ${company.id}`, value: company.id }));
    }

    get companyRoleOptions() {
        return this.roles
            .filter(role => this.isCompanyRole(role))
            .map(role => ({ label: role.roleName, value: role.roleId }));
    }

    canSubmitRequest(): boolean {
        return !!this.subjectCustomerId && !!this.targetCompanyId && this.requestedRoleIds.length > 0;
    }

    submitRequest(): void {
        if (!this.canSubmitRequest() || !this.subjectCustomerId || !this.targetCompanyId) {
            return;
        }
        this.sisterCompanyAccessService.create({
            subjectCustomerId: this.subjectCustomerId,
            targetCompanyId: this.targetCompanyId,
            requestedRoleIds: this.requestedRoleIds,
            reason: this.requestReason.trim() || undefined
        }).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Request submitted', detail: 'The target tenant administrator can now review the requested ERP access.' });
                this.targetCompanyId = undefined;
                this.requestedRoleIds = [];
                this.requestReason = '';
                this.loadRequests();
            },
            error: error => this.showError('Request not submitted', error, 'Could not submit sister-company access request.')
        });
    }

    beginReview(request: SisterCompanyAccessRequest): void {
        this.editingRequest = request;
        this.finalRoleIds = (request.status === 'APPROVED' ? request.approvedRoles : request.requestedRoles)
            .map(role => role.roleId)
            .filter((roleId): roleId is number => typeof roleId === 'number');
        this.decisionReason = request.decisionReason || '';
    }

    cancelReview(): void {
        this.editingRequest = null;
        this.finalRoleIds = [];
        this.decisionReason = '';
    }

    approve(request: SisterCompanyAccessRequest): void {
        if (this.finalRoleIds.length === 0) {
            return;
        }
        this.sisterCompanyAccessService.approve(request.id, { roleIds: this.finalRoleIds, reason: this.decisionReason.trim() || undefined }).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Access approved', detail: 'The employee can refresh tenant access and switch to this tenant.' });
                this.cancelReview();
                this.loadRequests();
            },
            error: error => this.showError('Approval failed', error, 'Could not approve sister-company access.')
        });
    }

    reject(request: SisterCompanyAccessRequest): void {
        const reason = window.prompt(`Reject access for ${request.subjectCustomerName || 'this employee'}? An optional reason will be recorded.`);
        if (reason === null) {
            return;
        }
        this.sisterCompanyAccessService.reject(request.id, reason || undefined).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Request rejected', detail: 'The request was closed without granting tenant access.' });
                this.cancelReview();
                this.loadRequests();
            },
            error: error => this.showError('Rejection failed', error, 'Could not reject sister-company access.')
        });
    }

    amendRoles(request: SisterCompanyAccessRequest): void {
        if (this.finalRoleIds.length === 0) {
            return;
        }
        this.sisterCompanyAccessService.amendRoles(request.id, { roleIds: this.finalRoleIds, reason: this.decisionReason.trim() || undefined }).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Roles updated', detail: 'The approved tenant roles were updated.' });
                this.cancelReview();
                this.loadRequests();
            },
            error: error => this.showError('Role update failed', error, 'Could not update sister-company roles.')
        });
    }

    revoke(request: SisterCompanyAccessRequest): void {
        if (!window.confirm(`Revoke ${request.subjectCustomerName || 'this employee'}'s access to ${request.targetCompanyName || 'this tenant'}?`)) {
            return;
        }
        this.sisterCompanyAccessService.revoke(request.id).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Access revoked', detail: 'The employee can no longer switch to this tenant.' });
                this.cancelReview();
                this.loadRequests();
            },
            error: error => this.showError('Revocation failed', error, 'Could not revoke sister-company access.')
        });
    }

    refreshTenantAccess(): void {
        this.tenantScopeService.initializeCurrentAccess().subscribe({
            next: summary => this.messageService.add({
                severity: summary ? 'success' : 'warn',
                summary: summary ? 'Tenant access refreshed' : 'Tenant access unavailable',
                detail: summary ? 'Any newly approved tenant is now available in the tenant selector.' : 'Please try again shortly.'
            }),
            error: error => this.showError('Refresh failed', error, 'Could not refresh tenant access.')
        });
    }

    loadRequests(): void {
        this.sisterCompanyAccessService.getOutbox().subscribe({
            next: requests => this.outbox = requests ?? [],
            error: error => this.showError('Outgoing requests unavailable', error, 'Could not load outgoing sister-company requests.')
        });
        if (this.context?.canManageIncoming) {
            this.sisterCompanyAccessService.getInbox().subscribe({
                next: requests => this.inbox = requests ?? [],
                error: error => this.showError('Incoming requests unavailable', error, 'Could not load incoming sister-company requests.')
            });
        } else {
            this.inbox = [];
        }
    }

    statusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        switch (status) {
            case 'APPROVED': return 'success';
            case 'PENDING': return 'warn';
            case 'REJECTED': return 'secondary';
            case 'REVOKED':
            case 'INVALIDATED':
            case 'EXPIRED': return 'danger';
            default: return 'info';
        }
    }

    private loadContext(): void {
        this.sisterCompanyAccessService.getContext().subscribe({
            next: context => {
                this.context = context;
                this.subjectCustomerId = this.sessionStorageService.getCustomer()?.id;
                this.loadRequests();
            },
            error: error => this.showError('Sister-company access unavailable', error, 'Could not load the approved sister-company access area.')
        });
    }

    private customerLabel(customer: SisterCompanyAccessRequestableCustomer): string {
        const name = customer.customerName?.trim() || `Customer ${customer.customerId}`;
        return customer.customerEmail ? `${name} (${customer.customerEmail})` : name;
    }

    private isCompanyRole(role: Role): boolean {
        const scope = (role.roleScope || '').trim().toUpperCase();
        const hasPortalPermission = (role.permissions ?? []).some(permission =>
            (permission.permissionCode || '').trim().toLowerCase().startsWith('portal.')
        );
        return (!scope || scope === 'COMPANY') && !hasPortalPermission;
    }

    private showError(summary: string, error: any, fallback: string): void {
        this.messageService.add({ severity: 'error', summary, detail: error?.error?.message || fallback });
    }
}
